import { SEO } from '@/components/seo'

export default function PrivacyPolicyPage() {
  return (
    <>
      <SEO
        title="Privacy Policy - WealthSpace | Data Protection"
        description="WealthSpace Privacy Policy. Learn how we collect, use, and protect your data with our proprietary privacy layer and UK GDPR compliance."
        url="/privacy-policy"
      />

      {/* Hero Section */}
      <section className="py-[80px] px-6 max-w-[900px] mx-auto">
        <h1 className="font-serif text-[clamp(36px,5vw,48px)] font-normal leading-[1.15] tracking-[-0.02em] text-brand-light mb-4">
          Privacy <span className="text-accent-teal italic">Policy</span>
        </h1>
        <p className="text-lg text-brand-muted leading-[1.7] max-w-[700px]">
          At WealthSpace, protecting your data is fundamental to everything we do. This policy explains how we collect, use, and safeguard your information in compliance with UK GDPR and data protection regulations.
        </p>
        <p className="text-sm text-slate-400 mt-4">Last updated: January 2026</p>
      </section>

      {/* Privacy Content */}
      <main className="px-6 pb-[80px] max-w-[900px] mx-auto">

        {/* Introduction */}
        <section className="mb-12">
          <h2 className="font-serif text-[28px] font-normal text-brand-light mb-5 pb-3 border-b border-slate-200">
            Introduction
          </h2>
          <p className="text-base text-brand-muted leading-[1.8] mb-4">
            WealthSpace Ltd (Company No. 15396707) ("WealthSpace", "we", "us", or "our") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services.
          </p>
          <p className="text-base text-brand-muted leading-[1.8]">
            We are registered in England and Wales and act as a data controller for the personal data we process. We are committed to ensuring that your information is protected in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
          </p>
        </section>

        {/* Proprietary Privacy Layer */}
        <section className="mb-12">
          <div className="bg-gradient-to-br from-accent-teal/10 to-accent-teal/5 border border-accent-teal/30 rounded-xl p-6 my-6">
            <h4 className="font-serif text-lg font-medium text-accent-teal mb-3">
              Our Proprietary Privacy Layer
            </h4>
            <p className="text-[15px] text-brand-muted leading-[1.7]">
              WealthSpace has developed a proprietary privacy layer specifically designed to protect client data when using AI-powered features. Unlike other platforms, we do not share your client data with external AI companies whose business models rely on data consumption. Your client information is processed securely within our controlled infrastructure, ensuring that sensitive financial data remains protected at all times.
            </p>
          </div>
        </section>

        {/* What Data We Collect */}
        <section className="mb-12">
          <h2 className="font-serif text-[28px] font-normal text-brand-light mb-5 pb-3 border-b border-slate-200">
            What Data We <span className="text-accent-teal">Collect</span>
          </h2>
          <p className="text-base text-brand-muted leading-[1.8] mb-4">
            We collect different types of information depending on how you interact with our services:
          </p>

          <p className="text-base text-brand-muted leading-[1.8] mb-4">
            <strong className="text-brand-light">Account Information</strong>
          </p>
          <ul className="list-none m-0 p-0 mb-4">
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              Name, email address, and contact details
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              Company name and business address
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              FCA registration details (where applicable)
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              Payment and billing information
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              Login credentials and authentication data
            </li>
          </ul>

          <p className="text-base text-brand-muted leading-[1.8] mb-4">
            <strong className="text-brand-light">Client Data (processed on your behalf)</strong>
          </p>
          <ul className="list-none m-0 p-0 mb-4">
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              Client names and contact information
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              Financial information and portfolio details
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              Meeting notes and transcriptions
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              Documents uploaded to the platform
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              Generated reports and recommendations
            </li>
          </ul>

          <p className="text-base text-brand-muted leading-[1.8] mb-4">
            <strong className="text-brand-light">Usage Data</strong>
          </p>
          <ul className="list-none m-0 p-0">
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              Log data including IP address, browser type, and device information
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              Pages visited and features used within the platform
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              Time spent on the platform and interaction patterns
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              Error reports and diagnostic data
            </li>
          </ul>
        </section>

        {/* How We Use Your Data */}
        <section className="mb-12">
          <h2 className="font-serif text-[28px] font-normal text-brand-light mb-5 pb-3 border-b border-slate-200">
            How We <span className="text-accent-teal">Use</span> Your Data
          </h2>
          <p className="text-base text-brand-muted leading-[1.8] mb-4">
            We use the information we collect for the following purposes:
          </p>
          <ul className="list-none m-0 p-0 mb-4">
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Service Delivery:</strong> To provide, maintain, and improve our platform and services
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">AI Processing:</strong> To power our AI features including transcription, report generation, and analysis - all within our secure privacy layer
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Account Management:</strong> To manage your account, process payments, and provide customer support
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Communications:</strong> To send service updates, security alerts, and important notices
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Product Improvement:</strong> To analyse usage patterns and improve our platform (using anonymised and aggregated data only)
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Legal Compliance:</strong> To comply with legal obligations and protect our rights
            </li>
          </ul>
          <p className="text-base text-brand-muted leading-[1.8]">
            We process your data based on contractual necessity (to provide our services), legitimate interests (to improve our services), legal obligations, and where applicable, your consent.
          </p>
        </section>

        {/* Data Storage and Security */}
        <section className="mb-12">
          <h2 className="font-serif text-[28px] font-normal text-brand-light mb-5 pb-3 border-b border-slate-200">
            Data Storage and <span className="text-accent-teal">Security</span>
          </h2>
          <p className="text-base text-brand-muted leading-[1.8] mb-4">
            We take the security of your data seriously and implement appropriate technical and organisational measures to protect it:
          </p>
          <ul className="list-none m-0 p-0 mb-4">
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Encryption:</strong> All data is encrypted in transit (TLS 1.3) and at rest (AES-256)
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Access Controls:</strong> Strict role-based access controls and multi-factor authentication
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Infrastructure:</strong> Data is hosted on enterprise-grade cloud infrastructure within the UK/EEA
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Monitoring:</strong> Continuous security monitoring and periodic security testing
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Backups:</strong> Regular encrypted backups with secure disaster recovery procedures
            </li>
          </ul>
          <p className="text-base text-brand-muted leading-[1.8]">
            We retain your data for as long as your account is active or as needed to provide services. After account closure, we retain data for a period necessary to comply with legal obligations and resolve disputes, after which it is securely deleted.
          </p>
        </section>

        {/* Your Rights (GDPR) */}
        <section className="mb-12">
          <h2 className="font-serif text-[28px] font-normal text-brand-light mb-5 pb-3 border-b border-slate-200">
            Your Rights <span className="text-accent-teal">(GDPR)</span>
          </h2>
          <p className="text-base text-brand-muted leading-[1.8] mb-4">
            Under UK GDPR, you have the following rights regarding your personal data:
          </p>
          <ul className="list-none m-0 p-0 mb-4">
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Right of Access:</strong> You can request a copy of the personal data we hold about you
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Right to Rectification:</strong> You can request correction of inaccurate or incomplete data
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Right to Erasure:</strong> You can request deletion of your data in certain circumstances
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Right to Restrict Processing:</strong> You can request that we limit how we use your data
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Right to Data Portability:</strong> You can request your data in a structured, machine-readable format
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Right to Object:</strong> You can object to processing based on legitimate interests or for marketing purposes
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Rights Related to Automated Decision-Making:</strong> You have rights concerning any automated decisions that significantly affect you
            </li>
          </ul>
          <p className="text-base text-brand-muted leading-[1.8]">
            To exercise any of these rights, please contact us using the details provided below. We will respond to your request within one month.
          </p>
        </section>

        {/* Cookies */}
        <section className="mb-12">
          <h2 className="font-serif text-[28px] font-normal text-brand-light mb-5 pb-3 border-b border-slate-200">
            Cookies
          </h2>
          <p className="text-base text-brand-muted leading-[1.8] mb-4">
            We use cookies and similar technologies to enhance your experience on our platform:
          </p>
          <ul className="list-none m-0 p-0 mb-4">
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Essential Cookies:</strong> Required for the platform to function correctly (authentication, security)
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Functional Cookies:</strong> Remember your preferences and settings
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Analytics Cookies:</strong> Help us understand how you use our platform to improve it
            </li>
          </ul>
          <p className="text-base text-brand-muted leading-[1.8]">
            You can manage cookie preferences through your browser settings. Note that disabling certain cookies may affect the functionality of our services.
          </p>
        </section>

        {/* Third Parties */}
        <section className="mb-12">
          <h2 className="font-serif text-[28px] font-normal text-brand-light mb-5 pb-3 border-b border-slate-200">
            Third <span className="text-accent-teal">Parties</span>
          </h2>
          <p className="text-base text-brand-muted leading-[1.8] mb-4">
            We work with carefully selected third parties to deliver our services. These include:
          </p>
          <ul className="list-none m-0 p-0 mb-4">
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Cloud Infrastructure Providers:</strong> For secure hosting and data storage
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Payment Processors:</strong> To handle subscription payments securely
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Analytics Services:</strong> To understand and improve platform usage
            </li>
            <li className="text-base text-brand-muted leading-[1.8] pl-6 relative mb-2 before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-2 before:h-2 before:bg-accent-teal before:rounded-full">
              <strong className="text-brand-light">Customer Support Tools:</strong> To provide you with assistance
            </li>
          </ul>
          <p className="text-base text-brand-muted leading-[1.8] mb-4">
            All third parties are bound by data processing agreements and are required to process your data only as instructed by us and in compliance with applicable data protection laws.
          </p>
          <p className="text-base text-brand-muted leading-[1.8]">
            <strong className="text-brand-light">Important:</strong> We do not sell your personal data to third parties. We do not share client data with external AI companies for training or other purposes outside of delivering our services to you.
          </p>
        </section>

        {/* International Transfers */}
        <section className="mb-12">
          <h2 className="font-serif text-[28px] font-normal text-brand-light mb-5 pb-3 border-b border-slate-200">
            International Transfers
          </h2>
          <p className="text-base text-brand-muted leading-[1.8]">
            We primarily store and process data within the UK and European Economic Area (EEA). Where transfers outside these regions are necessary, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses approved by the UK Information Commissioner's Office (ICO), to protect your data.
          </p>
        </section>

        {/* Changes to This Policy */}
        <section className="mb-12">
          <h2 className="font-serif text-[28px] font-normal text-brand-light mb-5 pb-3 border-b border-slate-200">
            Changes to This Policy
          </h2>
          <p className="text-base text-brand-muted leading-[1.8]">
            We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website and, where appropriate, by email. We encourage you to review this page periodically.
          </p>
        </section>

        {/* Contact for Privacy Matters */}
        <section className="mb-12">
          <h2 className="font-serif text-[28px] font-normal text-brand-light mb-5 pb-3 border-b border-slate-200">
            Contact for Privacy <span className="text-accent-teal">Matters</span>
          </h2>
          <p className="text-base text-brand-muted leading-[1.8] mb-4">
            If you have any questions about this Privacy Policy, wish to exercise your rights, or have concerns about how we handle your data, please contact us:
          </p>
          <div className="bg-white border border-slate-200 rounded-xl p-6 mt-6 shadow-card">
            <h4 className="text-base font-semibold text-brand-light mb-3">
              WealthSpace Ltd (Company No. 15396707)
            </h4>
            <p className="text-[15px] text-brand-muted leading-[1.7]">
              <strong className="text-brand-light">Email:</strong>{' '}
              <a href="mailto:support@wealthspace.ai" className="text-accent-teal hover:text-teal-300 transition-colors">
                support@wealthspace.ai
              </a>
              <br />
              <strong className="text-brand-light">Phone:</strong>{' '}
              <a href="tel:01225541226" className="text-accent-teal hover:text-teal-300 transition-colors">
                01225 541226
              </a>
              <br />
              <strong className="text-brand-light">Address:</strong> 2nd & 3rd Floors Northgate House, Upper Borough Walls, Bath, BA1 1RG
              <br />
              <strong className="text-brand-light">Company Registration:</strong> Registered in England & Wales
            </p>
          </div>
          <p className="text-base text-brand-muted leading-[1.8] mt-6">
            You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you believe your data protection rights have been violated. Visit{' '}
            <a
              href="https://ico.org.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-teal hover:text-teal-300 transition-colors"
            >
              ico.org.uk
            </a>{' '}
            for more information.
          </p>
        </section>

      </main>
    </>
  )
}
